import React, { useState, useRef } from "react";
import cn from "classnames";
import useOutsideClick from "../../../Ecommerce/Shared/hooks/useOutsideClick.hook";
import { CollectionNewSearchPageTranslations } from "../../CollectionNewSearchPageTranslations.csharp";
import { CollectionYearFilter } from "./CollectionYearFilter/CollectionSearchYearFilter";
import { CollectionClassificationFilter } from "./CollectionClassificationFilter";
import { SearchParams } from "../../CollectionNewSearchPage";
import { CollectionNestedFilter } from "./CollectionNestedFilter/CollectionNestedFilter";
import { MotifRelationship } from "../../MotifRelationship";

interface ICollectionFilter {
    translations: CollectionNewSearchPageTranslations;
    setMinYear: React.Dispatch<React.SetStateAction<number>>;
    setMaxYear: React.Dispatch<React.SetStateAction<number>>;
    minYear: number;
    maxYear: number;
    activeFilters: number;
    searchParams: SearchParams;
    setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
    filterAllMotifs: MotifRelationship | null;
    filterAllThemes: MotifRelationship | null;
    filterAllExhibitions?: MotifRelationship | null;
    motifFacetCounts: { [key: string]: number };
    themeFacetCounts: { [key: string]: number };
    exhibitionFacetCounts?: { [key: string]: number };
    totalCount: number;
    filterIsOpen: boolean;
    setFilterIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CollectionFilter = ({
    translations,
    setMinYear,
    setMaxYear,
    minYear,
    maxYear,
    activeFilters,
    setSearchParams,
    searchParams,
    filterAllMotifs,
    filterAllThemes,
    filterAllExhibitions,
    motifFacetCounts,
    themeFacetCounts,
    exhibitionFacetCounts = {},
    totalCount,
    filterIsOpen,
    setFilterIsOpen,
}: ICollectionFilter) => {
    const outsideRef = useRef();
    const classificationsKeyValues = [
        { key: "", value: translations.classificationAll },
        { key: "Malerier", value: translations.classificationPainting },
        { key: "Grafikk", value: translations.classificationGraphic },
        { key: "Tegninger", value: translations.classificationDrawing },
        { key: "Skulpturer", value: translations.classificationSculpture },
        { key: "Fotografier", value: translations.classificationPhoto },
    ];

    const toggleFilters = () => {
        setFilterIsOpen(!filterIsOpen);
    };

    const clearFilters = () => {
        setSearchParams({
            ...searchParams,
            classification: "",
            themes: [],
            motifs: [],
            exhibitions: [],
        });

        setMinYear(1863);
        setMaxYear(1944);
    };

    const handleClassificationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const key = event.target.value;
        setSearchParams({ ...searchParams, classification: key });
    };

    const handleThemesChange = (selectedThemes: string[]) => {
        setSearchParams({ ...searchParams, themes: selectedThemes });
    };

    const handleMotifsChange = (selectedMotifs: string[]) => {
        setSearchParams({ ...searchParams, motifs: selectedMotifs });
    };

    const handleExhibitionsChange = (selectedExhibitions: string[]) => {
        setSearchParams({ ...searchParams, exhibitions: selectedExhibitions });
    };

    useOutsideClick(outsideRef, () => {
        filterIsOpen && setFilterIsOpen(false);
    });

    React.useEffect(() => {
        if (filterIsOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
    }, [filterIsOpen]);
    return (
        <div className="CollectionFilter">
            <div className="CollectionFilter__container">
                <button
                    onClick={toggleFilters}
                    className={cn("CollectionFilter__button", { "CollectionFilter__button--active": activeFilters !== 0 })}
                >
                    {translations.filter}
                    {activeFilters !== 0 && " (" + activeFilters + ")"}
                    <div className="CollectionFilter__button-icon"></div>
                </button>
                {activeFilters !== 0 && (
                    <button onClick={clearFilters} className="CollectionFilter__clear-button">
                        {translations.clearFilter}
                    </button>
                )}
            </div>

            <div
                className={cn("CollectionFilter__filter-wrapper", {
                    "CollectionFilter__filter-wrapper--is-open": filterIsOpen,
                })}
            >
                <div
                    ref={outsideRef}
                    className={cn("CollectionFilter__filter", { "CollectionFilter__filter--is-open": filterIsOpen })}
                >
                    <div className="CollectionFilter__header">
                        <h2 className="CollectionFilter__heading">{translations.filter}</h2>
                        {activeFilters !== 0 && (
                            <button onClick={clearFilters} className="CollectionFilter__clear-button">
                                {translations.clearFilter}
                            </button>
                        )}
                        <button
                            aria-label={translations.filterDrawerCloseLabel}
                            className="CollectionFilter__top-close-button"
                            onClick={toggleFilters}
                        ></button>
                    </div>
                    <div className="CollectionFilter__content">
                        <form className="CollectionFilter__form" onSubmit={(e) => e.preventDefault()}>
                            <fieldset className="CollectionFilter__fieldset">
                                <legend className="CollectionFilter__legend">{translations.filterHeaderTechnique}</legend>
                                <CollectionClassificationFilter
                                    classificationsKeyValues={classificationsKeyValues}
                                    selectedClassification={searchParams.classification}
                                    handleClassificationChange={handleClassificationChange}
                                />
                            </fieldset>
                            <fieldset className="CollectionFilter__fieldset">
                                <CollectionYearFilter
                                    minYear={1863}
                                    maxYear={1944}
                                    setMinYear={setMinYear}
                                    setMaxYear={setMaxYear}
                                    currentMaxYear={maxYear}
                                    currentMinYear={minYear}
                                    setSearchParams={setSearchParams}
                                    searchParams={searchParams}
                                    translations={translations}
                                />
                            </fieldset>
                            <fieldset className="CollectionFilter__fieldset">
                                {filterAllMotifs && (
                                    <CollectionNestedFilter
                                        key="motifs"
                                        filterAllMotifs={filterAllMotifs.children}
                                        filterSelected={searchParams.motifs}
                                        setFilterSelected={handleMotifsChange}
                                        translations={translations}
                                        collapsibleName={filterAllMotifs.parentName}
                                        facetCounts={motifFacetCounts}
                                    />
                                )}
                                {filterAllThemes && (
                                    <CollectionNestedFilter
                                        key="themes"
                                        filterAllMotifs={filterAllThemes.children}
                                        filterSelected={searchParams.themes}
                                        setFilterSelected={handleThemesChange}
                                        translations={translations}
                                        collapsibleName={filterAllThemes.parentName}
                                        facetCounts={themeFacetCounts}
                                    />
                                )}
                                {filterAllExhibitions && (
                                    <CollectionNestedFilter
                                        key="exhibitions"
                                        filterAllMotifs={filterAllExhibitions.children}
                                        filterSelected={searchParams.exhibitions}
                                        setFilterSelected={handleExhibitionsChange}
                                        translations={translations}
                                        collapsibleName={filterAllExhibitions.parentName}
                                        facetCounts={exhibitionFacetCounts}
                                    />
                                )}
                            </fieldset>
                        </form>
                    </div>
                    <div className="CollectionFilter__button-container">
                        <button className="CollectionFilter__close-button" onClick={() => setFilterIsOpen(false)}>
                            {translations.filterShowXResults
                                .replace("{0}", totalCount.toLocaleString("en-US").replace(/,/g, " "))}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
